import { holdPropsInUtc, PushNotification } from "@repo/helpers";
import { KeyedArray, SerializedFile } from "@repo/models";
import { useCallback } from "react";

import { useRepContext } from "../contexts/editableItemContext";
import { useSettingsQuery } from "../helpers/queryHooks";
import { BackgroundCheck } from "../models";
import { UploadedFile } from "../models/backgroundCheck";
import RepIdentificationRequest from "../models/repIdentificationRequest";
import ApiRoute from "./apiRoute";
import * as Queries from "./queries";
import { BackgroundCheckDocumentTypes, useRepApi, useRepDocumentApi } from "./repApi";
import { useFilterItemV2QueryWrapper, useQueryWrapper, useRepPortalApi } from "./repPortalApi";

export function useBackgroundCheckApi() {
	const { Get, Post } = useRepPortalApi();
	const { saveRepDocument, deleteRepDocument } = useRepDocumentApi();
	const { repId } = useRepContext();
	const [settings] = useSettingsQuery();

	const saveBackgroundCheck = useCallback(
		function (bgc: BackgroundCheck) {
			const {
				consentDocument,
				consentDocumentUploaded,
				resultDocument,
				resultDocumentUploaded,
				driversLicenseDocumentFront,
				driversLicenseDocumentFrontUploaded,
				driversLicenseDocumentBack,
				driversLicenseDocumentBackUploaded,
				exceptionDocument,
				exceptionDocumentUploaded,
				...backgroundCheck
			} = bgc;

			return Post<number>("SaveBackgroundCheck", null, { representativeId: repId, ...backgroundCheck });
		},
		[Post],
	);

	const sendExceptionEmail = useCallback((id: number) => Post("SendBackgroundCheckExceptionEmail", { id }), [Post]);

	const saveBackgroundCheckDoc = useCallback(
		function (repId: number, file: SerializedFile, bgcId: number, docType: BackgroundCheckDocumentTypes) {
			return saveRepDocument(repId, file, docType, bgcId);
		},
		[Post],
	);
	const deleteBackgroundCheckDoc = useCallback(
		function (documentId: number) {
			return deleteRepDocument(documentId);
		},
		[Post],
	);
	const saveBackgroundCheckDocs = useCallback(
		async function (repId: number, bgcId: number, bgc: BackgroundCheck) {
			await Promise.all([
				deleteDocument(bgc.consentDocumentUploaded),
				deleteDocument(bgc.resultDocumentUploaded),
				deleteDocument(bgc.exceptionDocumentUploaded),
				deleteDocument(bgc.driversLicenseDocumentFrontUploaded),
				deleteDocument(bgc.driversLicenseDocumentBackUploaded),
			]);
			await Promise.all([
				saveDocument(bgc.consentDocument, BackgroundCheckDocumentTypes.BackgroundCheckConsentDocument),
				saveDocument(bgc.resultDocument, BackgroundCheckDocumentTypes.BackgroundCheckResultDocument),
				saveDocument(bgc.exceptionDocument, BackgroundCheckDocumentTypes.ExceptionDocument),
				saveDocument(bgc.driversLicenseDocumentFront, BackgroundCheckDocumentTypes.DriversLicenseDocumentFront),
				saveDocument(bgc.driversLicenseDocumentBack, BackgroundCheckDocumentTypes.DriversLicenseDocumentBack),
			]);

			async function saveDocument(files: SerializedFile[], docType: BackgroundCheckDocumentTypes) {
				if (!files) return;
				for (const file of files) await saveBackgroundCheckDoc(repId, file, bgcId, docType);
			}
			async function deleteDocument(files: UploadedFile[]) {
				if (!files) return;
				for (const file of files.filter((f) => f?.deleted)) await deleteBackgroundCheckDoc(file.id);
			}
		},
		[saveBackgroundCheckDoc, deleteBackgroundCheckDoc],
	);

	const initialize = useCallback(
		function (bgc: BackgroundCheck) {
			bgc.consentDocumentUploaded = getFiles(bgc.consentDocumentId, "Consent Document");
			bgc.resultDocumentUploaded = getFiles(bgc.resultDocumentId, "Result Document");
			bgc.exceptionDocumentUploaded = getFiles(bgc.exceptionDocumentId, "Exception Document");
			bgc.driversLicenseDocumentBackUploaded = getFiles(bgc.driversLicenseDocumentBackId, "Image");
			bgc.driversLicenseDocumentFrontUploaded = getFiles(bgc.driversLicenseDocumentFrontId, "Image");

			holdPropsInUtc(bgc, "requestDate", "responseDate");
			if (settings.timesInLocal) holdPropsInUtc(bgc, "createdOn", "updatedOn");

			if (!bgc.responseDate) {
				bgc.passedCriminalHistoryCheck = null;
				bgc.passedProhibitedPartiesListsCheck = null;
				bgc.passedStateCriminalCheck = null;
				bgc.passedStateSpecificCheck = null;
				bgc.passedSexOffenderRegistriesListCheck = null;
				bgc.passedSSNTrace = null;
				bgc.passedAllCountyCheck = null;
				bgc.passedDRCOffenderCheck = null;
			}

			if (settings.backgroundCheckSettings?.expirationDays) {
				const bgcDate = new Date(bgc.requestDate ?? bgc.createdOn ?? new Date());
				const expirationDate = new Date();
				expirationDate.setDate(expirationDate.getDate() - settings.backgroundCheckSettings.expirationDays);
				bgc.expired = bgcDate < expirationDate;
			}

			return bgc;

			function getFiles(fileId: number, name: string) {
				if (!fileId) return [];
				return [getFile(fileId, name)];
			}
			function getFile(fileId: number, name: string) {
				if (!fileId) return null;
				return {
					id: fileId,
					name,
					url: `${ApiRoute}/GetRepDocument/${fileId}`,
					type: "png",
				} as SerializedFile;
			}
		},
		[settings.timesInLocal, settings.backgroundCheckSettings?.expirationDays],
	);

	const loadBgc = useCallback(
		(bgcId: number) => Get<BackgroundCheck>("GetBackgroundCheck", { bgcId }).then(initialize),
		[Get, initialize],
	);

	const getBackgroundChecks = useCallback(
		async function (representativeId: number) {
			const bgcs = await Get<BackgroundCheck[]>("BackgroundChecks", {
				representativeId,
			});
			return bgcs.map(initialize);
		},
		[Get, initialize],
	);

	const loadBackgroundChecks = useCallback(
		function (repId: number) {
			return async function () {
				if (!repId) return defaultArr();
				try {
					const bgcs = await getBackgroundChecks(repId);
					return new KeyedArray<BackgroundCheck>(bgcs, (b) => b.backgroundCheckId);
				} catch (e) {
					PushNotification("Unable to retrieve background checks");
					return defaultArr();
				}
			};
		},
		[getBackgroundChecks],
	);

	const copyToNewSegment = useCallback(
		async function (backgroundCheckId: number, segmentId: number) {
			return await Post<number, null>("CopyBackgroundCheckToSegment", {
				backgroundCheckId,
				segmentId,
			});
		},
		[Post],
	);

	const associateToNewSegment = useCallback(
		async function (backgroundCheckId: number, segmentId: number) {
			return await Post<number, null>("AssociateBackgroundCheckToSegment", {
				backgroundCheckId,
				segmentId,
			});
		},
		[Post],
	);

	return {
		saveBackgroundCheckDocs,
		sendExceptionEmail,
		saveBackgroundCheck,
		loadBgc,
		getBackgroundChecks,
		loadBackgroundChecks,
		copyToNewSegment,
		associateToNewSegment,
	};
}

export function useBackgroundCheckQueries() {
	const { repId } = useRepContext();
	const { getScreeningProviders } = useRepApi();
	const { loadBackgroundChecks } = useBackgroundCheckApi();

	const useBackgroundCheckQuery = () =>
		useQueryWrapper(Queries.BACKGROUND_CHECKS, [repId, loadBackgroundChecks], loadBackgroundChecks(repId));

	const useScreeningProvidersQuery = () =>
		useFilterItemV2QueryWrapper(
			Queries.BACKGROUND_CHECK_SCREENING_PROVIDERS,
			[repId, getScreeningProviders],
			getScreeningProviders(repId),
		);

	return { useBackgroundCheckQuery, useScreeningProvidersQuery };
}

const defaultArr = () => new KeyedArray<BackgroundCheck>([], (b) => b.backgroundCheckId);

export function useIdentificationRequestApi() {
	const { Get } = useRepPortalApi();
	const { repId } = useRepContext();
	const getByRep = useCallback(
		() => Get<RepIdentificationRequest[]>("GetIdentificationRequests", { repId }),
		[Get, repId],
	);
	const create = useCallback(
		() =>
			Get<RepIdentificationRequest[]>("SendIdentificationRequest", {
				repId,
			}),
		[Get, repId],
	);

	const update = useCallback(
		(action: string, params?: object) => Get<RepIdentificationRequest[]>(action, params),
		[Get],
	);
	const updateFn = useCallback((action: string) => (requestId: number) => update(action, { requestId }), [update]);
	const resend = useCallback(updateFn("ResendIdentificationRequest"), [updateFn]);
	const cancel = useCallback(updateFn("CancelIdentificationRequest"), [updateFn]);
	const getStatus = useCallback(updateFn("GetStatus"), [updateFn]);
	///helper APIs to simulate workflow; should be removed at some point
	const complete = useCallback(updateFn("CompleteIdentificationRequest"), [updateFn]);
	const expire = useCallback(updateFn("ExpireIdentificationRequest"), [updateFn]);

	const useIdRequestsQuery = () => useQueryWrapper(Queries.IDENTIFICATION_REQUESTS, [repId], getByRep);

	return {
		getByRep,
		create,
		resend,
		cancel,
		getStatus,
		complete,
		expire,
		useIdRequestsQuery,
	};
}
